/**
 * API Client with Authentication and Error Handling
 * 
 * Feature: 033-auth-complete (Phase 4 - User Story 2)
 * Feature: 034-global-error-handling (Phase 5 - US3, Phase 6 - US4, Phase 8 - US6)
 * Description: Axios instance with MSAL token injection, 401 handling, centralized error handling, telemetry, and retry logic
 * Constitution: v1.11.0
 */

import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError, AxiosResponse, AxiosRequestConfig } from 'axios';
import { mapAxiosError, isNetworkError, extractCorrelationId } from './errorMapper';
import { ErrorCodes, ErrorSeverity } from '../../../shared/types/errors';
import { FrontendError, RetryConfig, DEFAULT_RETRY_CONFIG } from '../types/error';
import { trackApiError } from './telemetryService';
import { retry, RetryResult } from '../utils/retry';
import { isRetryableError, getRetryRecommendation } from '../utils/retryableErrors';
import { isDevAuthBypass } from '../authConfig';
import { getDevUserRole, getDevAssumeUserId, getDevAssumeTenantId } from '../auth/devUserStore';
import { applyLicenceRefusal, installLicenceFetchObserver, isLicenceRefusal } from '../auth/licenceSession';
import { applyAccountRefusal } from '../auth/accountRefusal';

// Type for MSAL instance (will be set by setupAuthInterceptor)
let msalInstance: any = null;
let loginRequest: any = null;

// Toast function will be injected from component layer
let showToast: ((type: 'error' | 'warning' | 'info', message: string) => void) | null = null;

// Session expiry modal callback
let showSessionExpiryModal: (() => void) | null = null;

// Spec 286 US4 — the global-axios licence interceptor is registered once.
let globalLicenceInterceptor = false;

/**
 * Setup authentication interceptor with MSAL instance
 * Call this from AuthProvider after MSAL initialization
 */
export function setupAuthInterceptor(msal: any, request: any): void {
  msalInstance = msal;
  loginRequest = request;
  // Spec 286 US4 — services still on the GLOBAL axios instance (AuthProvider sets its Authorization header)
  // get the same mid-session licence switch as apiClient. Registered once (effects can run twice).
  if (!globalLicenceInterceptor) {
    axios.interceptors.response.use((response) => response, (err) => {
      applyLicenceRefusal(err);
      applyAccountRefusal(err);
      return Promise.reject(err);
    });
    // Senior review 2026-10-10 #9: and every direct fetch() caller, through one observer on the global fetch.
    installLicenceFetchObserver(globalThis);
    globalLicenceInterceptor = true;
  }
}

/**
 * Acquire the current bearer token for use in raw fetch/SSE calls.
 * Returns null in dev-bypass mode (no token needed there).
 */
export async function getAuthHeader(): Promise<string | null> {
  if (isDevAuthBypass) return null;
  if (!msalInstance || !loginRequest) return null;
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return null;
  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: loginRequest.scopes,
      account: accounts[0],
    });
    return response.idToken ? `Bearer ${response.idToken}` : null;
  } catch {
    return null;
  }
}

/**
 * Setup error handling callbacks
 * Call this from ToastProvider or App component
 * FR-048-054: Integrate toast notifications with API errors
 */
export function setupErrorHandlers(
  toastFn: (type: 'error' | 'warning' | 'info', message: string) => void,
  sessionModalFn?: () => void
): void {
  showToast = toastFn;
  showSessionExpiryModal = sessionModalFn || null;
}

// API timeout constant (FR-012: 30 seconds default)
const API_TIMEOUT_MS = 30_000;

// Basic API client for backward compatibility
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
  timeout: API_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for auth token injection
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Skip token injection for the bare liveness probes only, matched on the exact path. 🛑 This used to be an
    // endsWith(), which also exempted every PROTECTED route whose path ends in /health — /api/infrastructure/health
    // went out anonymous on every open of the Infrastructure Health screen (prod: 401 then the 401-retry; dev walk:
    // 403 ACCESS_DENIED). Found by the screen audit, 2026-10-28.
    const publicPaths = ['/health', '/ready', '/healthz', '/livez', '/readyz'];
    const urlPath = (config.url ?? '').replace(/^https?:\/\/[^/]+/, '').split('?')[0];
    if (publicPaths.includes(urlPath)) {
      return config;
    }

    // Spec 255 (dev testing): impersonate a chosen user for the multi-user alert flow. Sent in
    // BOTH auth modes; the backend honors it only in non-production. Real auth still attaches the
    // Bearer token below, so the server validates the real caller, then overrides the identity.
    const assumeUserId = getDevAssumeUserId();
    if (assumeUserId) {
      config.headers['X-Dev-Assume-User-Id'] = assumeUserId;
    }

    // 🛑 THE TENANT MUST TRAVEL WITH THE USER, OR THE BACKEND CANNOT FIND THEM.
    //    `userRepository.getById` is tenant-scoped (Spec 241), so assuming a user from another
    //    tenant requires that tenant to be bound for the lookup. Without this header the server
    //    searched the CALLER's tenant, found nothing, and silently kept the caller's identity —
    //    while the UI happily displayed the selected name. Identity from one tenant, data from
    //    another, and no error anywhere.
    const assumeTenantId = getDevAssumeTenantId();
    if (assumeTenantId) {
      config.headers['X-Dev-Assume-Tenant-Id'] = assumeTenantId;
    }

    // In dev bypass mode, skip MSAL token injection and send the dev role header
    if (isDevAuthBypass) {
      const devRole = getDevUserRole();
      if (devRole) {
        config.headers['X-Dev-User-Role'] = devRole;
      }
      return config;
    }

    // Protected endpoint - require MSAL auth
    if (!msalInstance || !loginRequest) {
      // MSAL not initialized yet - reject request to prevent 401 loops
      console.warn('API request blocked: MSAL not initialized', { url: config.url });
      return Promise.reject(new Error('Authentication not initialized. Please wait for login to complete.'));
    }

    // Get MSAL accounts
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      // No authenticated user - reject request to prevent 401 loops
      console.warn('API request blocked: No authenticated account', { url: config.url });
      return Promise.reject(new Error('Not authenticated. Please log in first.'));
    }

    // Acquire token for the authenticated user
    try {
      const response = await msalInstance.acquireTokenSilent({
        scopes: loginRequest.scopes,
        account: accounts[0],
      });
      
      if (response.idToken) {
        config.headers.Authorization = `Bearer ${response.idToken}`;
      } else {
        console.warn('API request blocked: No ID token received');
        return Promise.reject(new Error('Failed to acquire access token.'));
      }
    } catch (error) {
      console.warn('Failed to acquire token for API request:', error);
      // Token acquisition failed - reject to prevent sending unauthorized request
      return Promise.reject(new Error('Authentication failed. Please log in again.'));
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for 401 handling with token refresh and centralized error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Spec 286 US4 — a licence refusal switches the SPA to the notice / can't-verify page. It is not a
    // permission error (no toast) and not an outage (no /service-unavailable redirect), so it stops here.
    if (applyLicenceRefusal(error)) return Promise.reject(error);

    // An archived person is told why: no token refresh, no session-expired redirect.
    if (applyAccountRefusal(error)) return Promise.reject(error);

    // Map error to frontend format for consistent handling
    const frontendError = mapAxiosError(error);

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Attempt token refresh
      if (msalInstance && loginRequest) {
        try {
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            // Try silent refresh
            const response = await msalInstance.acquireTokenSilent({
              scopes: loginRequest.scopes,
              account: accounts[0],
              forceRefresh: true,
            });

            // Update authorization header
            if (response.idToken) {
              originalRequest.headers.Authorization = `Bearer ${response.idToken}`;
              
              // Retry original request with new token
              return apiClient(originalRequest);
            }
          }
        } catch (refreshError: any) {
          // Silent refresh failed, try interactive
          if (
            refreshError.errorCode === 'consent_required' ||
            refreshError.errorCode === 'interaction_required' ||
            refreshError.errorCode === 'login_required'
          ) {
            // FR-056: Show session expiry modal (033-auth integration)
            if (showSessionExpiryModal) {
              showSessionExpiryModal();
              return Promise.reject(error);
            }
            
            try {
              await msalInstance.acquireTokenRedirect({
                scopes: loginRequest.scopes,
              });
              // User will be redirected, request will be retried after redirect
              return Promise.reject(error);
            } catch (redirectError) {
              console.error('Token refresh redirect failed:', redirectError);
              // Redirect to login page as last resort
              window.location.href = '/login?reason=session_expired';
              return Promise.reject(error);
            }
          }
        }
      }

      // No MSAL instance or refresh failed
      // FR-050: Show 401 toast with re-auth prompt
      if (showToast && showSessionExpiryModal) {
        showSessionExpiryModal();
      } else if (showToast) {
        showToast('warning', 'Your session has expired. Please sign in again.');
        window.location.href = '/login?reason=session_expired';
      } else {
        console.warn('Authentication failed, redirecting to login');
        window.location.href = '/login?reason=session_expired';
      }
      
      return Promise.reject(error);
    }

    // FR-049: Handle 400 Bad Request
    if (error.response?.status === 400) {
      if (showToast) {
        const message = frontendError.message || 'Invalid request. Please check your input.';
        showToast('error', message);
      }
    }

    // FR-051: Handle 403 Forbidden
    if (error.response?.status === 403) {
      // T032: Handle ROLE_NOT_ASSIGNED - redirect to PendingApproval
      const responseData = error.response?.data as any;
      if (responseData?.code === 'ROLE_NOT_ASSIGNED') {
        console.warn('[API] User has no role assigned, redirecting to pending-approval page');
        if (!window.location.pathname.includes('/pending-approval')) {
          window.location.href = '/pending-approval';
        }
        return Promise.reject(error);
      }
      
      if (showToast) {
        showToast('error', 'You do not have permission to perform this action.');
      }
    }

    // FR-052: Handle 404 Not Found
    if (error.response?.status === 404) {
      if (showToast) {
        showToast('warning', 'The requested resource could not be found.');
      }
    }

    // T037: Handle 429 Rate Limit Exceeded
    if (error.response?.status === 429) {
      const responseData = error.response?.data as any;
      const retryAfter = responseData?.details?.retryAfter || 
        parseInt(error.response.headers?.['retry-after'] || '60', 10);
      
      console.warn(`[API] Rate limit exceeded (429), retry after ${retryAfter}s`);
      
      if (showToast) {
        showToast('warning', `Too many requests. Please wait ${retryAfter} seconds before retrying.`);
      }
      
      // Don't redirect - let the component handle retry logic
      return Promise.reject(error);
    }

    // FR-053: Handle 500 Internal Error
    if (error.response?.status && error.response.status >= 500) {
      // T021: Handle 503 Service Unavailable - redirect to ServiceUnavailable page
      if (error.response.status === 503) {
        console.warn('[API] Service unavailable (503), redirecting to service-unavailable page');
        // Only redirect if not already on service-unavailable page
        if (!window.location.pathname.includes('/service-unavailable')) {
          window.location.href = '/service-unavailable';
        }
        return Promise.reject(error);
      }
      
      if (showToast) {
        let message = 'An unexpected error occurred. Please try again.';
        if (frontendError.referenceId) {
          message += ` (Reference: ${frontendError.referenceId})`;
        }
        showToast('error', message);
      }
    }

    // T020: Handle request timeout (ECONNABORTED)
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      console.warn(`[API] Request timeout after ${API_TIMEOUT_MS / 1000}s`);
      if (showToast) {
        showToast('error', 'Request timed out. The server may be overloaded. Please try again.');
      }
      return Promise.reject(error);
    }

    // FR-054: Handle network errors (T021: also redirect to ServiceUnavailable)
    if (isNetworkError(error)) {
      console.warn('[API] Network error detected, may redirect to service-unavailable page');
      if (showToast) {
        showToast('error', 'Unable to connect. Please check your internet connection.');
      }
      // For persistent network errors, the ServiceUnavailable page handles auto-retry
      // Only redirect after multiple consecutive failures (handled by retry logic)
    }

    // FR-070: Log to telemetry service
    trackApiError(
      frontendError.statusCode,
      frontendError.code,
      frontendError.message,
      frontendError.correlationId,
      frontendError.referenceId
    );

    // Log to console for debugging
    console.error('[API Error]', {
      code: frontendError.code,
      status: frontendError.statusCode,
      message: frontendError.message,
      correlationId: frontendError.correlationId,
      referenceId: frontendError.referenceId,
    });

    return Promise.reject(error);
  }
);

// Export error type for type safety
export type ApiError = AxiosError;
export type { FrontendError };
/**
 * Retry callback for showing toast during retry attempts
 */
function handleRetryAttempt(attempt: number, error: Error, nextDelayMs: number): void {
  if (showToast) {
    const seconds = Math.round(nextDelayMs / 1000);
    showToast('info', `Retrying request... (attempt ${attempt + 1}, waiting ${seconds}s)`);
  }
}

/**
 * API request with automatic retry for transient failures (US6)
 * 
 * @param config - Axios request configuration
 * @param retryConfig - Optional retry configuration
 * @returns Promise with Axios response
 * 
 * @example
 * ```typescript
 * const response = await apiRequestWithRetry({
 *   method: 'GET',
 *   url: '/api/v1/workspaces',
 * });
 * ```
 */
export async function apiRequestWithRetry<T = any>(
  config: AxiosRequestConfig,
  retryConfig: Partial<RetryConfig> = {}
): Promise<AxiosResponse<T>> {
  const mergedConfig: RetryConfig = {
    ...DEFAULT_RETRY_CONFIG,
    ...retryConfig,
    // Spec 286: isRetryableError never retries a licence refusal (utils/retryableErrors.ts, the one shared rule).
    shouldRetry: (error: Error) => isRetryableError(error),
    onRetry: handleRetryAttempt,
  };

  const result = await retry<AxiosResponse<T>>(
    () => apiClient.request<T>(config),
    mergedConfig
  );

  if (result.success && result.data) {
    return result.data;
  }

  // A licence refusal stops on purpose (review round 3 #12): no outage and no retry budget, so not reported as one.
  if (isLicenceRefusal(result.error)) {
    throw result.error;
  }

  // All retries exhausted
  const recommendation = getRetryRecommendation(result.error);
  console.warn(`[API Retry] All retries exhausted after ${result.attempts} attempts:`, {
    reason: recommendation.reason,
    totalTime: result.totalTime,
    error: result.error?.message,
  });

  throw result.error;
}

/**
 * GET request with automatic retry
 */
export function getWithRetry<T = any>(
  url: string,
  config?: AxiosRequestConfig,
  retryConfig?: Partial<RetryConfig>
): Promise<AxiosResponse<T>> {
  return apiRequestWithRetry<T>({ ...config, method: 'GET', url }, retryConfig);
}

/**
 * POST request with automatic retry
 * Note: Only retries if the request is idempotent or safe to retry
 */
export function postWithRetry<T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
  retryConfig?: Partial<RetryConfig>
): Promise<AxiosResponse<T>> {
  return apiRequestWithRetry<T>({ ...config, method: 'POST', url, data }, retryConfig);
}

/**
 * PUT request with automatic retry
 */
export function putWithRetry<T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
  retryConfig?: Partial<RetryConfig>
): Promise<AxiosResponse<T>> {
  return apiRequestWithRetry<T>({ ...config, method: 'PUT', url, data }, retryConfig);
}

/**
 * DELETE request with automatic retry
 */
export function deleteWithRetry<T = any>(
  url: string,
  config?: AxiosRequestConfig,
  retryConfig?: Partial<RetryConfig>
): Promise<AxiosResponse<T>> {
  return apiRequestWithRetry<T>({ ...config, method: 'DELETE', url }, retryConfig);
}

/**
 * Creates a retry-enabled instance of the API client
 * All methods will automatically retry on transient failures
 */
export const apiClientWithRetry = {
  get: getWithRetry,
  post: postWithRetry,
  put: putWithRetry,
  delete: deleteWithRetry,
  request: apiRequestWithRetry,
};